import { useState, useEffect } from 'react';
import FormLayout from '../../../../components/auth/FormLayout';
import InviteAccountStep from '../../../../components/auth/steps/InviteAccountStep';
import LoadingStep from '../../../../components/auth/steps/LoadingStep';
import CompletionStep from '../../../../components/auth/steps/CompletionStep';
import useFetchInvitationsInfo from '../api/useFetchInvitationsInfo';
import useUpdateInvitationsInfo from '../api/useUpdateInvitationsInfo';

type Step = 'account' | 'loading' | 'completion';

export default function InviteAccountTemplate() {
  const [currentStep, setCurrentStep] = useState<Step>('account');
  const [invitationToken, setInvitationToken] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirm: '',
  });
  const { mutateAsync: updateInvitationsInfo } = useUpdateInvitationsInfo(invitationToken);

  // 招待情報を取得
  const {
    data: invitationInfo,
    isLoading: isLoadingInvitation,
    error: invitationError,
  } = useFetchInvitationsInfo(invitationToken);

  // URLパスから招待トークンを取得
  useEffect(() => {
    // URLパスから直接トークンを抽出
    const pathSegments = window.location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    if (lastSegment && lastSegment !== 'invite') {
      setInvitationToken(lastSegment);
      return;
    }

    // フォールバック: URLパラメータから取得
    const urlParams = new URLSearchParams(window.location.search);
    console.log('URL params:', urlParams);
    const token = urlParams.get('token');
    if (token) {
      setInvitationToken(token);
    }
  }, []);

  // 招待情報が取得できたら名前を設定
  useEffect(() => {
    if (invitationInfo?.target_user) {
      setFormData((prevData) => ({
        ...prevData,
        name: invitationInfo.target_user.name,
        email: invitationInfo.target_user.email,
      }));
    }
  }, [invitationInfo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // invitation[field] の形式から field を抽出
    const fieldName = name.includes('[') ? name.split('[')[1].replace(']', '') : name;

    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async () => {
    setCurrentStep('loading');
    try {
      await updateInvitationsInfo(formData);
      setCurrentStep('completion');
    } catch (error) {
      console.error('Failed to update invitation info:', error);
      setCurrentStep('account');
    }
  };

  const handleBack = () => {
    // 招待メールまたはログインページに戻る
    window.location.href = '/agent/account/login';
  };

  const handleLogin = () => {
    // ログインページに遷移
    window.location.href = '/agent/account/login';
  };

  const canProceed = () => {
    return formData.name && formData.password && formData.password_confirm;
  };

  const renderContent = () => {
    // 招待情報の取得中はローディング表示
    if (invitationToken && isLoadingInvitation) {
      return <LoadingStep />;
    }

    // 招待情報の取得でエラーが発生した場合
    if (invitationError) {
      console.error('Failed to fetch invitation info:', invitationError);
      // エラーの場合はログインページにリダイレクト
      window.location.href = '/agent/account/login';
      return <LoadingStep />;
    }

    switch (currentStep) {
      case 'account':
        return (
          <InviteAccountStep
            formData={formData}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            onBack={handleBack}
            canProceed={!!canProceed()}
            invitationToken={invitationToken}
          />
        );
      case 'loading':
        return <LoadingStep />;
      case 'completion':
        return <CompletionStep onLogin={handleLogin} />;
      default:
        return (
          <InviteAccountStep
            formData={formData}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            onBack={handleBack}
            canProceed={!!canProceed()}
            invitationToken={invitationToken}
          />
        );
    }
  };

  return <FormLayout>{renderContent()}</FormLayout>;
}
